/**
 * Lectures Table Configuration - Frontend
 * @description Column configuration for LecturesTable component
 */

import React from 'react';
import { TFunction } from 'i18next';
import { LecturesColumnConfig } from '@/types/table.types';
import { LecturesTableData } from '@/types/operational-ui.types';
import {
  LecturesNumberCell,
  LecturesCoordinatesCell,
  LecturesEventCell,
  LecturesSingleAnglesCell,
  LecturesSingleLimitCell,
  LectureOperationIdCell,
  LecturesTimestampCell,
  LecturesTimezoneCell,
} from '@/components/features/operations/lecturesTableCells';

export const getLecturesTableColumns = (
  t: TFunction,
  showSequence = false,
  showOperationId = false
): LecturesColumnConfig[] => [
  ...(showSequence
    ? [
        {
          id: 'sequence',
          accessor: 'sequence' as keyof LecturesTableData,
          header: t('pages.lectures.columns.sequence'),
          enableSorting: true,
          enableFilter: false,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          cell: (info: any) => <LecturesNumberCell info={info} />,
        },
      ]
    : []),
  ...(showOperationId
    ? [
        {
          id: 'operationId',
          accessor: 'operationId' as keyof LecturesTableData,
          header: t('pages.lectures.columns.operationId'),
          enableSorting: true,
          enableFilter: true,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          cell: (info: any) => <LectureOperationIdCell info={info} />,
        },
      ]
    : []),
  {
    id: 'date',
    accessor: 'timestamp' as keyof LecturesTableData,
    header: t('pages.lectures.columns.timestamp'),
    enableSorting: true,
    enableFilter: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: any) => <LecturesTimestampCell info={info} />,
  },
  {
    id: 'tz',
    accessor: 'tz' as keyof LecturesTableData,
    header: t('pages.lectures.columns.timezone'),
    enableSorting: false,
    enableFilter: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: any) => <LecturesTimezoneCell info={info} />,
  },
  /*
  {
    id: 'date',
    accessor: 'date' as keyof LecturesTableData,
    header: t('pages.lectures.columns.lectureDate'),
    enableSorting: true,
    enableFilter: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: any) => <LecturesDateCell info={info} />,
  },
  {
    id: 'time',
    accessor: 'time' as keyof LecturesTableData,
    header: t('pages.lectures.columns.lectureTime'),
    enableSorting: true,
    enableFilter: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: any) => <LecturesTimeCell info={info} />,
  },
  */
  {
    id: 'coordinates',
    accessor: 'coordinatesFormatted' as keyof LecturesTableData,
    header: t('pages.lectures.columns.coordinates'),
    enableSorting: false,
    enableFilter: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: any) => <LecturesCoordinatesCell info={info} />,
  },
  {
    id: 'angleLat',
    accessor: 'inclinationAngle_lateral' as keyof LecturesTableData,
    header: t('pages.lectures.columns.inclinationAngleLateral'),
    enableSorting: true,
    enableFilter: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: any) => <LecturesSingleAnglesCell info={info} />,
  },
  {
    id: 'angleLong',
    accessor: 'inclinationAngle_longitudinal' as keyof LecturesTableData,
    header: t('pages.lectures.columns.inclinationAngleLongitudinal'),
    enableSorting: true,
    enableFilter: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: any) => <LecturesSingleAnglesCell info={info} />,
  },
  /*
  {
    id: 'limits',
    accessor: 'latLimit',
    header: t('pages.lectures.columns.limits'),
    enableSorting: true,
    enableFilter: false,
    cell: (info) => <LecturesLimitsCell info={info} />,
  },
  */
  {
    id: 'angleLimitsLat',
    accessor: 'angleLimits_lateral' as keyof LecturesTableData,
    header: t('pages.lectures.columns.angleLimitsLateral'),
    enableSorting: true,
    enableFilter: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: any) => <LecturesSingleLimitCell info={info} />,
  },
  {
    id: 'angleLimitsLong',
    accessor: 'angleLimits_longitudinal' as keyof LecturesTableData,
    header: t('pages.lectures.columns.angleLimitsLongitudinal'),
    enableSorting: true,
    enableFilter: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: any) => <LecturesSingleLimitCell info={info} />,
  },
  {
    id: 'eventType',
    accessor: 'eventType' as keyof LecturesTableData,
    header: t('pages.lectures.columns.event'),
    enableSorting: true,
    enableFilter: true,
    filterType: 'select',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: any) => <LecturesEventCell info={info} />,
    getOptionLabel: (option: string) => t(`pages.lectures.events.${option}`, option),
  },
];
